// src/components/patterns/PatternList.js - Pattern list with filters
import { useState, useEffect } from 'react';
import api from '../../utils/api';
import PatternCard from './PatternCard';
import PatternDetail from './PatternDetail';

const PatternList = () => {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [filters, setFilters] = useState({
    pattern_type: '',
    severity: ''
  });

  useEffect(() => {
    loadPatterns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadPatterns = async () => {
    setLoading(true);
    try {
      // Get patterns from analytics dashboard
      const response = await api.get('/api/analytics/dashboard?period=monthly');
      let allPatterns = response.data.recent_patterns || [];

      // Apply filters
      if (filters.pattern_type) {
        allPatterns = allPatterns.filter(p => p.pattern_type === filters.pattern_type);
      }
      if (filters.severity) {
        allPatterns = allPatterns.filter(p => p.severity === filters.severity);
      }

      setPatterns(allPatterns);
    } catch (error) {
      console.error('Failed to load patterns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = (patternId) => {
    setPatterns(patterns.map(p => 
      p.id === patternId 
        ? { ...p, acknowledged_at: new Date().toISOString() }
        : p
    ));
    setSelectedPattern(null);
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
        <h2 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">Pattern Type</label>
            <select
              value={filters.pattern_type}
              onChange={(e) => setFilters({ ...filters, pattern_type: e.target.value })}
              className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
            >
              <option value="">All Types</option>
              <option value="similar_content">Similar Content</option>
              <option value="geographic_cluster">Geographic Cluster</option>
              <option value="repeated_source">Repeated Source</option>
              <option value="temporal_spike">Temporal Spike</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">Severity</label>
            <select
              value={filters.severity}
              onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
              className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ pattern_type: '', severity: '' })}
              className="w-full px-4 py-2 text-sm text-shayek-light-700 dark:text-shayek-dark-700 bg-shayek-light-100 dark:bg-shayek-dark-50 rounded-lg hover:bg-shayek-light-200 dark:hover:bg-shayek-dark-100 transition-colors border border-shayek-light-300 dark:border-shayek-dark-200"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Patterns List */}
      <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900">Detected Patterns ({patterns.length})</h2>
          <button
            onClick={loadPatterns}
            className="px-4 py-2 text-sm text-shayek-light-700 dark:text-shayek-dark-700 bg-shayek-light-100 dark:bg-shayek-dark-50 rounded-lg hover:bg-shayek-light-200 dark:hover:bg-shayek-dark-100 transition-colors border border-shayek-light-300 dark:border-shayek-dark-200"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-center text-shayek-light-500 dark:text-shayek-dark-500 py-8">Loading patterns...</div>
        ) : patterns.length === 0 ? (
          <div className="text-center text-shayek-light-500 dark:text-shayek-dark-500 py-8">No patterns found</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {patterns.map((pattern) => (
              <PatternCard
                key={pattern.id}
                pattern={pattern}
                onClick={() => setSelectedPattern(pattern)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pattern Detail Modal */}
      {selectedPattern && (
        <PatternDetail
          pattern={selectedPattern}
          onClose={() => setSelectedPattern(null)}
          onAcknowledge={handleAcknowledge}
        />
      )}
    </div>
  );
};

export default PatternList;

